import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, Phone, ChevronRight, Check, Bird, Calculator, RotateCcw, Star, Clock, Bug, AlertTriangle, ChevronLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Logo } from "../components/Layout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bird Calculator Component
function BirdCalculator() {
  const [panels, setPanels] = useState("");
  const [storeys, setStoreys] = useState("single");
  const [pigeonDuration, setPigeonDuration] = useState("<6m");
  const [addGutterCleaning, setAddGutterCleaning] = useState(false);
  const [addPanelWash, setAddPanelWash] = useState(false);
  const [addPressureWash, setAddPressureWash] = useState(false);
  const [addBirdMite, setAddBirdMite] = useState(false);
  const [dontKnowPanels, setDontKnowPanels] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const panelsRef = useRef(null);

  useEffect(() => {
    const panelCount = parseInt(panels) || 0;
    if (panelCount < 1) { setEstimate(null); return; }

    const tier1Rate = 42, tier2Rate = 45.50, tier3Rate = 49;
    const nestingAdd = pigeonDuration === ">6m" ? 2 : 0;
    
    let baseTotal, baseRate;
    if (panelCount <= 16) {
      baseTotal = panelCount * (tier1Rate + nestingAdd);
      baseRate = tier1Rate + nestingAdd;
    } else if (panelCount <= 26) {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + ((panelCount - 16) * (tier2Rate + nestingAdd));
      baseRate = tier2Rate + nestingAdd;
    } else {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + (10 * (tier2Rate + nestingAdd)) + ((panelCount - 26) * (tier3Rate + nestingAdd));
      baseRate = tier3Rate + nestingAdd;
    }
    
    let storeyMultiplier = 1, storeyAddition = 0;
    if (storeys === "two") storeyMultiplier = 1.25;
    else if (storeys === "three") { storeyMultiplier = 1.4; storeyAddition = 400; }
    
    const calculatedMesh = Math.round((baseTotal * storeyMultiplier) + storeyAddition);
    const meshTotal = Math.max(550, calculatedMesh);
    
    // Cleaning services pricing
    const storeyCleanMult = (storeys === "three" ? 1.40 : storeys === "two" ? 1.20 : 1);
    const storeyFlat = (storeys === "three" ? 80 : storeys === "two" ? 50 : 0);
    const panelWashLevy = (storeys === "three" ? 80 : storeys === "two" ? 50 : 0);
    const gutterCleaningPrice = Math.round((60 + panelCount * 8) * storeyCleanMult + storeyFlat);
    const panelWashPriceCalc = Math.round((25 + panelCount * 5) * storeyCleanMult + storeyFlat + panelWashLevy);
    const pressureWashPriceCalc = Math.round((50 + panelCount * 9) * storeyCleanMult + storeyFlat);

    const selectedCleaningCount = [addGutterCleaning, addPanelWash, addPressureWash].filter(Boolean).length;
    const isCleaningBundle = selectedCleaningCount >= 2;
    const rawCleaningTotal = (addGutterCleaning ? gutterCleaningPrice : 0) + (addPanelWash ? panelWashPriceCalc : 0) + (addPressureWash ? pressureWashPriceCalc : 0);
    const cleaningBundleDiscount = isCleaningBundle ? Math.round(rawCleaningTotal * 0.20) : 0;
    const cleaningTotal = rawCleaningTotal - cleaningBundleDiscount;

    const total = meshTotal + cleaningTotal + (addBirdMite ? 200 : 0);
    const launchDiscount = Math.round(total * 0.10);
    setEstimate({ panels: panelCount, meshTotal, gutterCleaningPrice, panelWashPrice: panelWashPriceCalc, pressureWashPrice: pressureWashPriceCalc, selectedCleaningCount, isCleaningBundle, cleaningBundleDiscount, cleaningTotal, birdMitePrice: addBirdMite ? 200 : 0, total, launchDiscount, discountedTotal: total - launchDiscount });
  }, [panels, storeys, pigeonDuration, addGutterCleaning, addPanelWash, addPressureWash, addBirdMite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!estimate && !dontKnowPanels) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/solar-estimate`, {
        ...formData, panels: estimate?.panels || 0, storeys, pigeonDuration,
        meshTotal: estimate?.meshTotal || 0, gutterCleaning: addGutterCleaning,
        gutterCleaningPrice: estimate?.gutterCleaningPrice || 0, panelWash: addPanelWash,
        panelWashPrice: estimate?.panelWashPrice || 0, pressureWash: addPressureWash,
        pressureWashPrice: estimate?.pressureWashPrice || 0, cleaningBundleDiscount: estimate?.cleaningBundleDiscount || 0,
        birdMiteAddon: addBirdMite, birdMitePrice: estimate?.birdMitePrice || 0,
        estimatedTotal: estimate?.total || 0, dontKnowPanels,
        source: "facebook_ad"
      });
      setSubmitted(true);
      toast.success("Quote request sent!");
    } catch { toast.error("Failed to send. Please call us."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setPanels(""); setStoreys("single"); setPigeonDuration("<6m"); setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); setAddBirdMite(false); setDontKnowPanels(false);
    setFormData({ name: "", phone: "", email: "", address: "" }); setSubmitted(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="bird-calculator">
      <div className="bg-[#5BC0DE] text-white p-5 flex items-center gap-3">
        <Bird className="w-6 h-6" />
        <h3 className="text-xl font-bold">Solar Panel Bird Proofing</h3>
      </div>
      
      <div className="grid md:grid-cols-2">
        <div className="p-6 md:p-8 space-y-5">
          <div>
            <Label className="font-medium mb-2 block text-base">Number of solar panels</Label>
            <Input ref={panelsRef} type="number" min="1" value={panels} onChange={(e) => { setPanels(e.target.value); setDontKnowPanels(false); }} placeholder="e.g., 20" className="text-lg py-5" data-testid="bird-panels-input" disabled={dontKnowPanels} />
            <button type="button" onClick={() => { setDontKnowPanels(!dontKnowPanels); if (!dontKnowPanels) setPanels(""); }} className={`mt-2 text-sm font-medium transition-colors ${dontKnowPanels ? 'text-[#7ABC32]' : 'text-[#5BC0DE] hover:text-[#3498aa]'}`} data-testid="dont-know-panels-btn">
              {dontKnowPanels ? "I know my panel count" : "I don't know my panel count"}
            </button>
          </div>
          
          <div>
            <Label className="font-medium mb-2 block text-base">House height</Label>
            <div className="grid grid-cols-3 gap-3">
              {[{ value: "single", label: "1 Storey" }, { value: "two", label: "2 Storey" }, { value: "three", label: "3+" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setStoreys(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${storeys === opt.value ? 'border-[#5BC0DE] bg-[#5BC0DE]/10 text-[#5BC0DE]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`bird-storey-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            {storeys === "two" && <p className="text-xs text-slate-500 mt-1.5 italic">*2 technicians required — harness & higher access</p>}
            {storeys === "three" && <p className="text-xs text-slate-500 mt-1.5 italic">*Boom lift hire, harness system & 2 techs required</p>}
          </div>
          
          <div>
            <Label className="font-medium mb-2 block text-base">How long have birds been nesting?</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "<6m", label: "<6 months" }, { value: ">6m", label: ">6 months" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => { setPigeonDuration(opt.value); if (opt.value === "<6m") { setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); setAddBirdMite(false); } }}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${pigeonDuration === opt.value ? 'border-[#5BC0DE] bg-[#5BC0DE]/10 text-[#5BC0DE]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`bird-duration-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {pigeonDuration === ">6m" && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#7ABC32] uppercase tracking-wide">Cleaning Services {estimate?.isCleaningBundle && <span className="ml-1 bg-[#7ABC32] text-white px-1.5 py-0.5 rounded-full normal-case text-[10px]">Bundle — save 20%!</span>}</p>
              {estimate?.selectedCleaningCount > 0 && estimate?.selectedCleaningCount < 2 && (
                <p className="text-xs text-amber-600 font-medium">Select 2+ for a 20% bundle discount</p>
              )}
              <div className="space-y-2">
                {[
                  { key: "gutter", active: addGutterCleaning, toggle: setAddGutterCleaning, label: "Gutter Clean", price: estimate?.gutterCleaningPrice, desc: "Full gutter flush", details: ["Remove all debris from gutters", "Flush & clear downpipes", "Dispose of waste material"] },
                  { key: "panel", active: addPanelWash, toggle: setAddPanelWash, label: "Panel Wash", price: estimate?.panelWashPrice, desc: "Surface wash panels", details: ["Water-fed broom clean on panel tops", "Remove bird droppings & residue", "Restore panel efficiency"] },
                  { key: "pressure", active: addPressureWash, toggle: setAddPressureWash, label: "Pressure Wash", price: estimate?.pressureWashPrice, desc: "Wash & sanitise", details: ["High-pressure wash around panels", "Sanitise & deodorise affected areas", "Remove staining from roof surface"] },
                ].map(opt => (
                  <label key={opt.key} className={`block p-2.5 rounded-lg cursor-pointer border-2 transition-colors ${opt.active ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} data-testid={`cleaning-${opt.key}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={opt.active} onCheckedChange={opt.toggle} />
                      <span className="font-medium text-sm whitespace-nowrap">{opt.label}</span>
                      <span className="text-xs text-slate-500 hidden sm:inline">— {opt.desc}</span>
                      {opt.price && <span className="text-[#7ABC32] font-bold text-xs ml-auto">${opt.price}</span>}
                    </div>
                    {opt.active && (
                      <ul className="mt-2 ml-8 space-y-0.5">
                        {opt.details.map((d, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />{d}</li>)}
                      </ul>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
          
          <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 ${addBirdMite ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-[#E74C3C]/5 border-[#E74C3C]/30'}`}>
            <Checkbox checked={addBirdMite} onCheckedChange={setAddBirdMite} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Bird Mite Treatment (+$200)</span>
              {addBirdMite && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Roof void dusting treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Specialist bird mite chemical treatment (internal & external)</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Follow-up call after 1 week to check results</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Free follow-up treatment if needed</li>
                </ul>
              )}
            </div>
          </label>
          
          {/* What's included in your bird proofing */}
          {estimate && (
            <div className="bg-[#5BC0DE]/5 border border-[#5BC0DE]/20 rounded-lg p-3">
              <p className="text-xs font-semibold text-[#5BC0DE] mb-1.5">What's included:</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#5BC0DE] flex-shrink-0" />Professional stainless steel solar proofing mesh with epoxy resin coating</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#5BC0DE] flex-shrink-0" />All clips, fasteners & materials</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#5BC0DE] flex-shrink-0" />Bird nest removal from under panels</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#5BC0DE] flex-shrink-0" />10-year warranty on mesh & install</li>
              </ul>
            </div>
          )}
        </div>
        
        <div className="bg-slate-50 p-6 md:p-8 border-t md:border-t-0 md:border-l">
          {!submitted ? (
            <>
              <div className={`rounded-xl overflow-hidden mb-5 ${(estimate || dontKnowPanels) ? 'bg-[#5BC0DE]' : 'bg-slate-300'}`}>
                <div className={`px-4 py-2.5 flex items-center justify-center ${(estimate || dontKnowPanels) ? 'bg-[#3498aa]' : 'bg-slate-400'}`}>
                  <img src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png" alt="Pest Free" className="h-10" />
                </div>
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #7ABC32, #5BC0DE)' }} />
                <div className="p-4 text-white text-center">
                  {dontKnowPanels ? (
                    <div>
                      <p className="text-lg font-bold mb-1">No worries!</p>
                      <p className="text-sm text-white/80">Tell us your details below and we'll call you with a free quote</p>
                    </div>
                  ) : estimate ? (
                    <>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-lg text-white/50 line-through">${estimate.total}</span>
                        <span className="text-3xl font-bold">${estimate.discountedTotal}</span>
                      </div>
                      <p className="text-amber-400 font-semibold text-sm">Save ${estimate.launchDiscount} (10% off)</p>
                      <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                      <div className="mt-2 text-left text-xs space-y-0.5 border-t border-white/20 pt-2">
                        <div className="flex justify-between"><span>Solar proofing mesh ({estimate.panels} panels)</span><span><span className="line-through text-white/40 mr-1">${estimate.meshTotal}</span>${Math.round(estimate.meshTotal * 0.9)}</span></div>
                        {estimate.cleaningTotal > 0 && <div className="flex justify-between"><span>Cleaning services</span><span>${estimate.cleaningTotal}</span></div>}
                        {estimate.isCleaningBundle && <div className="flex justify-between text-[#7ABC32]"><span>Bundle discount</span><span>incl.</span></div>}
                        {estimate.birdMitePrice > 0 && <div className="flex justify-between"><span>Bird mite treatment</span><span><span className="line-through text-white/40 mr-1">$200</span>${Math.round(200 * 0.9)}</span></div>}
                        <div className="text-white/50 text-[10px] pt-1 border-t border-white/10">Prices include GST</div>
                      </div>
                    </>
                  ) : <button type="button" onClick={() => { panelsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); panelsRef.current?.focus(); }} className="text-2xl font-bold py-2 cursor-pointer hover:text-white/80 transition-colors">Enter panels</button>}
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Name *" className="bg-white py-5" data-testid="bird-name" />
                  <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Phone *" className="bg-white py-5" data-testid="bird-phone" />
                </div>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Email *" className="bg-white py-5" data-testid="bird-email" />
                <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Address *" rows={2} className="bg-white" data-testid="bird-address" />
                <Button type="submit" disabled={(!estimate && !dontKnowPanels) || submitting} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 text-base font-semibold" data-testid="bird-submit">
                  {submitting ? "Sending..." : dontKnowPanels ? "Request a Callback" : "Get My Quote"} <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold mb-2">Quote Sent!</h4>
              <p className="text-slate-600 text-sm mb-4">We'll call within 30 mins</p>
              <Button onClick={reset} variant="outline" size="sm"><RotateCcw className="w-4 h-4 mr-1" /> New Quote</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Pest Calculator Component
function PestCalculator() {
  const [propertyType, setPropertyType] = useState("house");
  const [bedrooms, setBedrooms] = useState("3");
  const [storeys, setStoreys] = useState("single");
  const [treatmentType, setTreatmentType] = useState("standard");
  const [rodentTreatment, setRodentTreatment] = useState(false);
  const [termiteAddOn, setTermiteAddOn] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const basePrices = {
    house: { standard: { "1": 165, "2": 175, "3": 198, "4": 220, "5": 250, "6": 285 }, comprehensive: { "1": 220, "2": 240, "3": 280, "4": 330, "5": 380, "6": 450 } },
    unit: { standard: { "1": 150, "2": 170, "3": 190, "4": 210, "5": 240, "6": 270 }, comprehensive: { "1": 195, "2": 220, "3": 250, "4": 290, "5": 330, "6": 380 } }
  };

  const basePrice = basePrices[propertyType]?.[treatmentType]?.[bedrooms] || 198;
  let adjustedPrice = basePrice;
  if (storeys === "double") adjustedPrice += 30;
  
  let rodentPrice = 0;
  if (rodentTreatment) {
    rodentPrice = treatmentType === "comprehensive" ? 120 : 85;
    if (storeys === "double") rodentPrice += 20;
  }
  
  let termiteInspection = 0;
  if (termiteAddOn) {
    termiteInspection = 150;
    const bedroomNum = parseInt(bedrooms);
    if (bedroomNum >= 5) termiteInspection += 70;
    else if (bedroomNum >= 4) termiteInspection += 40;
    else if (bedroomNum >= 3) termiteInspection += 20;
    if (storeys === "double") termiteInspection += 60;
  }
  
  const totalPrice = adjustedPrice + rodentPrice + termiteInspection;
  const launchDiscount = Math.round(totalPrice * 0.10);
  const discountedPrice = totalPrice - launchDiscount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/pest-estimate`, {
        ...formData, propertyType, bedrooms, storeys, treatmentType,
        serviceFrequency: "oneoff", rodentTreatment, rodentPrice,
        termiteAddOn, termiteInspectionEstimate: termiteInspection || null,
        pricePerVisit: adjustedPrice, source: "facebook_ad"
      });
      setSubmitted(true);
      toast.success("Quote request sent!");
    } catch { toast.error("Failed to send. Please call us."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setPropertyType("house"); setBedrooms("3"); setStoreys("single");
    setTreatmentType("standard"); setRodentTreatment(false); setTermiteAddOn(false);
    setFormData({ name: "", phone: "", email: "", address: "" }); setSubmitted(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="pest-calculator">
      <div className="bg-[#E74C3C] text-white p-5 flex items-center gap-3">
        <Bug className="w-6 h-6" />
        <h3 className="text-xl font-bold">General Pest Control</h3>
      </div>
      
      <div className="grid md:grid-cols-2">
        <div className="p-6 md:p-8 space-y-5">
          <div>
            <Label className="font-medium mb-2 block text-base">Property Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "house", label: "House" }, { value: "unit", label: "Unit/Townhouse" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setPropertyType(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${propertyType === opt.value ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`pest-type-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <Label className="font-medium mb-2 block text-base">Bedrooms</Label>
            <div className="grid grid-cols-6 gap-2">
              {["1", "2", "3", "4", "5", "6"].map(num => (
                <button key={num} type="button" onClick={() => setBedrooms(num)}
                  className={`py-3 rounded-lg border-2 text-sm font-semibold transition-colors ${bedrooms === num ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`pest-bed-${num}`}>
                  {num}{num === "6" && "+"}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <Label className="font-medium mb-2 block text-base">House Height</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "single", label: "Single Storey" }, { value: "double", label: "Double Storey" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setStoreys(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${storeys === opt.value ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`pest-storey-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <Label className="font-medium mb-2 block text-base">Treatment</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "standard", label: "Standard" }, { value: "comprehensive", label: "Comprehensive" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setTreatmentType(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${treatmentType === opt.value ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`pest-treatment-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          <label className="flex items-start gap-2 p-3 bg-slate-100 rounded-lg cursor-pointer">
            <Checkbox checked={rodentTreatment} onCheckedChange={setRodentTreatment} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Rodent Treatment (+${rodentPrice || 85})</span>
              {rodentTreatment && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Internal & external bait stations</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Entry point identification</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Ongoing monitoring program</li>
                </ul>
              )}
            </div>
          </label>
          
          <label className="flex items-start gap-2 p-3 bg-[#D2691E]/5 border border-[#D2691E]/30 rounded-lg cursor-pointer">
            <Checkbox checked={termiteAddOn} onCheckedChange={setTermiteAddOn} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Yearly Termite Inspection (+${termiteInspection || 150})</span>
              {termiteAddOn && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Full property termite inspection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Thermal imaging & moisture detection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Detailed report with photos</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Early detection saves thousands</li>
                </ul>
              )}
            </div>
          </label>
          
          {/* What's included in your pest treatment */}
          <div className="bg-[#E74C3C]/5 border border-[#E74C3C]/20 rounded-lg p-3">
            <p className="text-xs font-semibold text-[#E74C3C] mb-1.5">
              {treatmentType === "standard" ? "Standard" : "Comprehensive"} treatment includes:
            </p>
            <ul className="space-y-1 text-xs text-slate-600">
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Internal & external spray treatment</li>
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Cockroaches, ants, spiders & silverfish</li>
              {treatmentType === "standard" ? (
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />6-month warranty</li>
              ) : (
                <>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Roof void & subfloor treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Webbing removal & flea treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />12-month warranty</li>
                </>
              )}
            </ul>
          </div>
        </div>
        
        <div className="bg-slate-50 p-6 md:p-8 border-t md:border-t-0 md:border-l">
          {!submitted ? (
            <>
              <div className="bg-[#E74C3C] rounded-xl overflow-hidden mb-5">
                <div className="bg-[#c0392b] px-4 py-2.5 flex items-center justify-center">
                  <img src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png" alt="Pest Free" className="h-10" />
                </div>
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #7ABC32, #5BC0DE)' }} />
                <div className="p-4 text-white text-center">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-lg text-white/50 line-through">${totalPrice}</span>
                    <span className="text-3xl font-bold">${discountedPrice}</span>
                    <span className="text-sm text-white/70">per visit</span>
                  </div>
                  <p className="text-amber-400 font-semibold text-sm">Save ${launchDiscount} (10% off)</p>
                  <p className="text-xs text-red-300 mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Name *" className="bg-white py-5" data-testid="pest-name" />
                  <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Phone *" className="bg-white py-5" data-testid="pest-phone" />
                </div>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Email *" className="bg-white py-5" data-testid="pest-email" />
                <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Address *" rows={2} className="bg-white" data-testid="pest-address" />
                <Button type="submit" disabled={submitting} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 text-base font-semibold" data-testid="pest-submit">
                  {submitting ? "Sending..." : "Get My Quote"} <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold mb-2">Quote Sent!</h4>
              <p className="text-slate-600 text-sm mb-4">We'll call within 30 mins</p>
              <Button onClick={reset} variant="outline" size="sm"><RotateCcw className="w-4 h-4 mr-1" /> New Quote</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Landing Page
export default function BirdProofingLandingPage() {
  return (
    <>
      <>
        <title>Get Your Price in 60 Seconds | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Instant quotes for bird proofing and pest control. 10% off launch special. 10-year warranty on solar proofing mesh." />
        <meta name="robots" content="noindex" />
      </>

      {/* Header */}
      <header className="bg-slate-900 py-4">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <Logo className="h-12" />
          <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white px-4 py-2 rounded-full font-medium">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">(07) 3708 1422</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #7ABC32, #5BC0DE)' }} />

      {/* Hero */}
      <section className="relative text-white min-h-[85vh] md:min-h-[70vh] flex flex-col px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_39d7dac4-671e-4bb1-b985-850f5777c447/artifacts/n88mo16e_20240704_102443_Original.jpeg" alt="" className="w-full h-full object-cover object-[center_60%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/60" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center pt-6 md:pt-8">
          <div className="inline-flex items-center gap-2 bg-[#7ABC32]/20 backdrop-blur-sm border border-[#7ABC32]/30 rounded-full px-5 py-2 mb-6">
            <span className="font-bold text-[#7ABC32]">LIMITED TIME</span>
            <span className="text-white/90">10% Off All Services</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-5 leading-tight drop-shadow-lg">
            Get Your <span className="text-[#7ABC32]">Instant Quote</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto drop-shadow">
            Professional pest control & solar panel bird proofing with 10-year warranty
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <a href="#bird-calc" className="inline-flex items-center gap-2 bg-[#5BC0DE] hover:bg-[#4AB0CE] text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:scale-105">
              <Bird className="w-6 h-6" /> Bird Proofing Quote
            </a>
            <a href="#pest-calc" className="inline-flex items-center gap-2 bg-[#E74C3C] hover:bg-[#D63B2C] text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:scale-105">
              <Bug className="w-6 h-6" /> Pest Control Quote
            </a>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm md:text-base">
            <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#7ABC32]" /><span>10-Year Warranty</span></div>
            <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#5BC0DE]" /><span>Same-Week Service</span></div>
            <div className="flex items-center gap-2"><Star className="w-5 h-5 text-yellow-400 fill-yellow-400" /><span>4.9 Google Rating</span></div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-b py-4 px-4">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-6 md:gap-10 items-center">
          <div className="flex items-center gap-2">
            <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
            <span className="text-sm font-semibold text-slate-700">4.9 Google Rating</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#7ABC32]" />
            <span className="text-sm font-semibold text-slate-700">QBCC Licensed #15366887</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-[#5BC0DE]" />
            <span className="text-sm font-semibold text-slate-700">Fully Insured</span>
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-10 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 text-center mb-6">Our Recent Work</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[
              { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/z5jjmahy_IMG_1795.jpeg", alt: "Mesh & clip close-up" },
              { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/uyek99ws_IMG_5432.jpeg", alt: "Mesh along roof edge" },
              { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/1g87oe1v_IMG_0886.png", alt: "Install on new panels" },
              { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/tet1ecjw_IMG_8783.jpeg", alt: "Panel cleaning" },
              { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/7avvtv1a_IMG_0716.jpeg", alt: "Cleaned panels with mesh" },
              { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/w6vcbets_IMG_1037.jpeg", alt: "Early bird proofing", pos: "bottom" },
            ].map((p, i) => (
              <div key={i} className="rounded-lg overflow-hidden aspect-square shadow">
                <img src={p.url} alt={p.alt} className="w-full h-full object-cover" style={p.pos ? {objectPosition: p.pos} : undefined} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculators */}
      <section className="py-12 md:py-16 px-4 bg-slate-100">
        <div className="max-w-5xl mx-auto space-y-12">
          <div id="bird-calc"><BirdCalculator /></div>
          
          {/* Why You Need Bird Proofing - between calculators for urgency */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-slate-800 text-white p-5 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
              <h3 className="text-xl font-bold">What Happens Without Bird Proofing</h3>
            </div>
            <div className="p-6 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/33gyj04l_IMG_9478.jpeg", caption: "Bird mites killing pigeons under panels" },
                  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/o0eug5x9_IMG_9479.jpeg", caption: "Cockroaches & lice living in bird nests" },
                  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/4jsedrwx_IMG_0695.png", caption: "Blocked gutters causing water damage" },
                  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/h62hrxig_IMG_1139.jpeg", caption: "Droppings covering roof & panels" },
                  { url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/uby7luqb_IMG_9231.jpeg", caption: "Pigeons nesting under panels" },
                  { url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/0mtr8qr6_IMG_5249.jpeg", caption: "Shelter & breeding ground for birds" },
                  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/orqq6r1b_IMG_6862.jpeg", caption: "Damaged bird netting & droppings on building" },
                  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/w1rw9fzr_IMG_5474.jpeg", caption: "Bird droppings covering solar panels & tiles" },
                ].map((p, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden aspect-[4/3] shadow">
                    <img src={p.url} alt={p.caption} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                      <p className="text-white text-xs font-medium">{p.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 text-center">
                <p className="text-slate-600 text-sm mb-3">Bird mites, cockroaches, and lice can enter your home through the roof. Don't wait until it's too late.</p>
                <a href="#bird-calc" className="inline-flex items-center gap-2 bg-[#5BC0DE] hover:bg-[#4AB0CE] text-white px-6 py-3 rounded-full font-semibold transition-colors">
                  <Bird className="w-5 h-5" /> Get Your Bird Proofing Quote
                </a>
              </div>
            </div>
          </div>

          <div id="pest-calc"><PestCalculator /></div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-8 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-5">
            <h2 className="text-lg font-bold text-slate-800">4.9 Stars from 50+ Happy Customers</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: "Donna M.", text: "Great communication and thorough work. Pigeons gone after 2 years!", area: "Ripley", bg: "bg-slate-900 text-white" },
              { name: "Andrew C.", text: "Quick response, fair price, thorough clean up. Highly recommend.", area: "Springfield", bg: "bg-white text-slate-900 border border-slate-200" },
              { name: "Andrew B.", text: "Extremely professional on a large commercial bird netting project. 5 stars.", area: "Brisbane", bg: "bg-[#7ABC32] text-white" },
              { name: "Sarah T.", text: "Punctual, clean and very reasonable price. Couldn't be happier.", area: "Ipswich", bg: "bg-slate-900 text-white" },
            ].map((review, i) => (
              <div key={i} className={`rounded-2xl p-4 ${review.bg}`}>
                <div className="flex text-yellow-400 mb-2">{[...Array(5)].map((_, j) => <Star key={j} className="w-3 h-3 fill-current" />)}</div>
                <p className="text-xs leading-relaxed mb-2">"{review.text}"</p>
                <p className="text-[10px] font-semibold opacity-70">{review.name} — {review.area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call CTA */}
      <section className="bg-slate-900 py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-white/80 mb-3">Prefer to talk? Get a quote over the phone.</p>
          <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white px-8 py-4 rounded-full text-lg font-semibold">
            <Phone className="w-5 h-5" /> Call (07) 3708 1422
          </a>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 py-3 px-4 md:hidden z-50">
        <div className="flex gap-2">
          <a href="tel:0737081422" className="flex-1 flex items-center justify-center gap-2 bg-[#7ABC32] text-white py-3 rounded-full font-semibold text-sm">
            <Phone className="w-4 h-4" /> Call Now
          </a>
          <a href="#bird-calc" className="flex-1 flex items-center justify-center gap-2 bg-[#5BC0DE] text-white py-3 rounded-full font-semibold text-sm">
            <Calculator className="w-4 h-4" /> Get Quote
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 py-6 px-4 text-center text-white/60 text-sm pb-24 md:pb-6">
        <p>© 2025 Pest Free Ipswich & Brisbane. QBCC Licensed #15366887. Fully Insured.</p>
      </footer>
    </>
  );
}
